import { Box, Table, TableHead } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import {
  BaseItem,
  Column,
  RenderTopItemList,
  StickyGroupConfig,
  VirtualizedTableHeaderAction,
} from '../VirtualizedTable.types';
import { useTableScrollerContext } from '../scroller/useTableScroller';
import { useStyles } from '../virtualizedTable.styles';
import { FIXED_TABLE_HEADER_ID } from '../virtualizedTableIds';
import StickyGroupBands from './StickyGroupBands';
import VirtualizedTableFixedHeader from './VIrtualizedTableFixedHeader';

export interface VirtualizedTableHeaderProps<T extends BaseItem> {
  columns: Column<T>[];
  hasRowActions: boolean;
  headerAction?: VirtualizedTableHeaderAction;
  hasRowCheckbox: boolean;
  onToggleSelectAll?: () => void;
  isAllSelected?: boolean;
  onClickHideColumn?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disableCheckBox?: boolean;
  ExtraHeader?: React.ReactNode;
  renderTopItemList?: RenderTopItemList<T>;
  fixedTopOffset?: number;
  stickyGroup?: StickyGroupConfig<T>;
  stickyGroupOverlayRef?: (element: HTMLElement | null) => void;
}

const SPACE_OCCUPIED_BY_THE_APP_BAR = 112;

const VirtualizedTableHeader = <T extends BaseItem>({
  columns,
  hasRowActions,
  headerAction,
  hasRowCheckbox,
  onToggleSelectAll,
  isAllSelected,
  onClickHideColumn,
  disableCheckBox,
  ExtraHeader,
  renderTopItemList,
  fixedTopOffset = SPACE_OCCUPIED_BY_THE_APP_BAR,
  stickyGroup,
  stickyGroupOverlayRef,
}: VirtualizedTableHeaderProps<T>) => {
  const { handleAddRefToSyncScroll } = useTableScrollerContext();
  const { classes } = useStyles();

  return (
    <>
      {ExtraHeader && (
        <Box
          className={classes.extraHeader}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: 1,
          }}
        >
          {ExtraHeader}
        </Box>
      )}
      <Box
        className={classNames(classes.fixedHeaderContainer, {
          [classes.stickyGroupHeaderContainer]: !!stickyGroup,
        })}
        sx={(theme) => ({
          top: fixedTopOffset,
          ...(renderTopItemList && { paddingBottom: theme.spacing(0.5) }),
        })}
      >
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <div className={classes.fixedHeader} ref={handleAddRefToSyncScroll}>
            <Table className={classes.table} id={FIXED_TABLE_HEADER_ID}>
              <TableHead>
                <VirtualizedTableFixedHeader
                  columns={columns}
                  hasRowActions={hasRowActions}
                  headerAction={headerAction}
                  hasRowCheckbox={hasRowCheckbox}
                  onToggleSelectAll={onToggleSelectAll}
                  isAllSelected={isAllSelected}
                  onClickHideColumn={onClickHideColumn}
                  disableCheckBox={disableCheckBox}
                  hasExtraHeader={!!ExtraHeader}
                />
                {renderTopItemList &&
                  renderTopItemList({
                    visibleColumns: columns,
                    className: classes.headerCell,
                  })}
              </TableHead>
            </Table>
          </div>
        </Box>
        {stickyGroup && (
          <Box
            className={classes.stickyGroupOverlay}
            ref={stickyGroupOverlayRef}
            sx={{ height: stickyGroup.bands.length * stickyGroup.bandHeight }}
          >
            <StickyGroupBands<T>
              bands={stickyGroup.bands}
              bandHeight={stickyGroup.bandHeight}
              columns={columns}
              hasRowCheckbox={hasRowCheckbox}
              hasRowActions={hasRowActions}
              hasHideColumnsButton={!!onClickHideColumn}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default VirtualizedTableHeader;
